import { Controller, useFormContext } from 'react-hook-form'
import range from 'lodash/range'
import uniq from 'lodash/uniq'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { KlearDate } from '@klear/date'
import { CalendarQuarter } from '@klear/date/calendar-quarter'
import type { DocumentForm } from '@klear/document/wire.client'
import { Label } from '@klear/ui/components/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'

const YEARS_BACK = 5
const QUARTERS = [1, 2, 3, 4]

export function PeriodEndFields() {
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext<DocumentForm>()
  const today = KlearDate.today()
  const latestYear = CalendarQuarter.of({ year: today.yearValue, quarter: 1 }).hasEnded(today)
    ? today.yearValue
    : today.yearValue - 1
  const selectedYear = toNumber(watch('periodEndYear'))
  const years = uniq([
    ...range(latestYear, latestYear - YEARS_BACK - 1, -1),
    ...(selectedYear > 0 ? [selectedYear] : [])
  ])
  const errorMessage = errors.periodEndQuarter?.message
  const fileCount = watch('file')?.length ?? 0

  return (
    <div className="space-y-2">
      <Label>Period ending *</Label>
      <div className="grid grid-cols-2 gap-3">
        <Controller
          control={control}
          name="periodEndYear"
          render={({ field }) => (
            <Select value={field.value ?? ''} onValueChange={field.onChange}>
              <SelectTrigger aria-label="Period-ending year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={toString(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <Controller
          control={control}
          name="periodEndQuarter"
          render={({ field }) => (
            <Select value={field.value ?? ''} onValueChange={field.onChange}>
              <SelectTrigger aria-label="Period-ending quarter">
                <SelectValue placeholder="Quarter" />
              </SelectTrigger>
              <SelectContent>
                {QUARTERS.map((quarter) => (
                  <SelectItem
                    key={quarter}
                    value={toString(quarter)}
                    disabled={selectedYear > 0 && !CalendarQuarter.of({ year: selectedYear, quarter }).hasEnded(today)}
                  >
                    Q{quarter}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      {errorMessage ? (
        <p className="text-sm text-destructive">{errorMessage}</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {fileCount > 1
            ? 'Every file gets this period, so upload other quarters separately.'
            : 'The quarter the statement period ends in. A 2025 annual statement ends in 2025 Q4.'}
        </p>
      )}
    </div>
  )
}
